import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Asset, AssetFolder } from '@vidmakr/bindings/types';
import {
  ChevronRight,
  Copy,
  Download,
  Eye,
  Folder,
  FolderPlus,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  RefreshCcw,
  Search,
  Trash2,
  Upload,
  MoveRight,
  Lock,
  X,
  ArrowRightLeft,
  Server,
  Link2,
  ChevronDown,
  HardDrive,
  Replace,
  ImagePlay,
} from 'lucide-react';
import { customFontFamily, formatDuration, isFolderLinkedKey, parseReplacement, sortRows, uuidv7, type SortValue } from '@vidmakr/shared';
import { Button, ConfirmModal, EmptyState, Input, Menu, Modal, PageHeader, Select, Spinner, cx, type MenuItem } from '@/components/ui';
import { openContextMenu, type ContextEntry } from '@/components/ContextMenu';
import { AssetThumb } from '@/components/AssetThumb';
import { InspectorPanel, InspectorToggle, SortTh, ViewToggle, rowTone, tableCls } from '@/components/Browse';
import { AssetsInspector, kindLabel, type FolderStats } from '@/components/Inspectors';
import { useOrg } from '@/lib/hooks';
import { useSpaces } from '@/lib/spaces';
import { AccessDialog } from '@/components/Spaces';
import { ConvertDialog } from '@/components/ConvertDialog';
import { GifDialog } from '@/components/GifDialog';
import { reducers } from '@/lib/stdb';
import { attempt, errorMessage, toast } from '@/lib/toast';
import { UPLOAD_ACCEPT, cancelUpload, replaceFile, uploadFiles, useUploads, type UploadInput } from '@/lib/upload';
import { DrivesDialog } from '@/components/DrivesPanel';
import { ensureCustomFont } from '@/lib/fonts';
import { formatBytes, timeAgo } from '@/lib/format';
import { mediaUrls } from '@/lib/media';
import { api } from '@/lib/api';
import { SelectCheck, plural, useDragMove, useSelection } from '@/lib/selection';
import { downloadZip } from '@/lib/zipDownload';
import { ShareAssetDialog, type AssetShareTab } from '@/components/ReviewLinks';
import { AssetReviewBadge } from '@/components/AssetReview';
import { MessagesSquare, Send, Share2 } from 'lucide-react';
import { openSendTo, useIntegrations } from '@/components/Integrations';
import { useInspector, useSort, useViewMode } from '@/lib/browse';
import { MEDIA_DRAG, mediaMoveProblem, moveMediaItems, useMediaIndex, type MediaDest } from '@/lib/library';

type AssetRow = Asset;
type FolderRow = AssetFolder;

/** Selection keys: `f:<folder id>` or `a:<asset id>`. */
type Key = string;
/** Where a move puts things: a folder (which decides the library), or the top of a library (undefined = where each item is). */
type Dest = MediaDest;

const SORT_KEYS = ['name', 'type', 'duration', 'resolution', 'size', 'createdBy', 'added'] as const;
type SortKey = (typeof SORT_KEYS)[number];

/**
 * The asset library: files and folders of the organization's libraries (the
 * shared one, each space's, each project's), as a table (the default) or a
 * grid, with a details panel on the right.
 * - The library and folder are in the URL (`?lib=space:<id>&folder=<id>`), so
 *   the sidebar can link to them.
 * - Click selects (Shift: range, Ctrl/⌘: toggle); drag on empty space to
 *   select with a box; double-click opens. Ctrl/⌘+A, Esc, Delete and F2 work too.
 * - Drag the selection onto a folder, a breadcrumb or a sidebar folder to move it there.
 * - `?share=<asset id>` (with `&comment=<id>`) opens a file's share dialog at
 *   that comment's conversation (notifications link here).
 */
export function AssetsPage() {
  const { orgId } = useParams();
  const { org } = useOrg(orgId);
  const sp = useSpaces(orgId);
  const [assets, ready] = useTable(tables.asset.where((a) => a.orgId.eq(orgId ?? '')));
  const [folders] = useTable(tables.assetFolder.where((f) => f.orgId.eq(orgId ?? '')));
  const [projects] = useTable(tables.project.where((p) => p.orgId.eq(orgId ?? '')));
  const [jobs] = useTable(tables.job.where((j) => j.orgId.eq(orgId ?? '')));
  const [usage] = useTable(tables.projectAsset.where((r) => r.orgId.eq(orgId ?? '')));
  const [reviewRows] = useTable(tables.assetReview.where((r) => r.orgId.eq(orgId ?? '')));
  const reviews = useMemo(() => new Map(reviewRows.map((r) => [r.assetId, r])), [reviewRows]);
  const uploads = useUploads((s) => s.items);
  const [params, setParams] = useSearchParams();
  // Libraries: 'all', 'library' (shared), 'space:<id>', 'project:<id>'.
  const scope = params.get('lib') || 'all';
  const folderId = params.get('folder') ?? '';
  const go = (lib: string, folder = '') => {
    const next = new URLSearchParams();
    if (lib !== 'all') next.set('lib', lib);
    if (folder) next.set('folder', folder);
    setParams(next);
  };
  const setScope = (lib: string) => go(lib);
  const setFolderId = (id: string) => go(scope, id);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<AssetRow | null>(null);
  const [sharing, setSharing] = useState<AssetRow | null>(null);
  const navigate = useNavigate();
  const [shareTab, setShareTab] = useState<AssetShareTab>('links');
  // Opened from a notification: ?share=<asset id>&comment=<comment id>.
  const shareParam = params.get('share') ?? '';
  const commentParam = params.get('comment') ?? '';
  useEffect(() => {
    if (!shareParam || !ready || sharing?.id === shareParam) return;
    const a = assets.find((x) => x.id === shareParam);
    if (a) setSharing(a);
    else {
      toast.error('That file is no longer available');
      closeShare();
    }
  }, [shareParam, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeShare = () => {
    setSharing(null);
    if (!params.has('share')) return;
    const next = new URLSearchParams(params);
    next.delete('share');
    next.delete('comment');
    setParams(next, { replace: true });
  };
  const [converting, setConverting] = useState<AssetRow[] | null>(null);
  const [gifFrom, setGifFrom] = useState<AssetRow | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string; type: 'asset' | 'folder' } | null>(null);
  const [moving, setMoving] = useState<Key[] | null>(null);
  const [deleting, setDeleting] = useState<Key[] | null>(null);
  const [view, setView] = useViewMode('assets');
  const [sort, onSort] = useSort<SortKey>('assets', SORT_KEYS, { key: 'added', dir: 'desc' });
  const inspector = useInspector();
  const fileInput = useRef<HTMLInputElement>(null);
  // "Replace file…": the picker below, for this asset.
  const replaceInput = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<AssetRow | null>(null);
  const [drives, setDrives] = useState(false);
  const index = useMediaIndex(assets, projects, usage);

  const jobProgress = useMemo(() => {
    const m = new Map<string, number>();
    for (const j of jobs) if ((j.kind === 'media' || j.kind === 'import') && j.status === 'running') m.set(j.assetId, j.progress);
    return m;
  }, [jobs]);
  // A ready file being processed again stays playable, so its own status does not change:
  // its newest media job says whether it waits for a worker, runs, or failed.
  const reprocessing = useMemo(() => {
    const m = new Map<string, { status: string; progress: number; error: string; at: bigint }>();
    for (const j of jobs) {
      if (j.kind !== 'media' || !['queued', 'running', 'failed'].includes(j.status)) continue;
      const at = j.createdAt.microsSinceUnixEpoch;
      const prev = m.get(j.assetId);
      if (!prev || at > prev.at) m.set(j.assetId, { status: j.status, progress: j.progress, error: j.error, at });
    }
    // A later job that finished hides an older failure.
    for (const j of jobs) {
      const r = m.get(j.assetId);
      if (r && j.kind === 'media' && j.status === 'done' && j.createdAt.microsSinceUnixEpoch > r.at) m.delete(j.assetId);
    }
    return m;
  }, [jobs]);
  const progressOf = (a: AssetRow) => {
    const up = uploads[a.id];
    const uploading = a.status === 'uploading' || parseReplacement(a.replacement)?.status === 'uploading';
    return uploading && up ? up.loaded / Math.max(1, up.size) : jobProgress.get(a.id);
  };

  const scopeProjectId = scope.startsWith('project:') ? scope.slice(8) : '';
  const scopeSpaceId = scope.startsWith('space:') ? scope.slice(6) : '';
  const inScope = (a: { projectId: string; spaceId: string }) =>
    scope === 'all' ||
    (scope === 'library' ? a.projectId === '' && a.spaceId === '' : scopeSpaceId ? a.projectId === '' && a.spaceId === scopeSpaceId : a.projectId === scopeProjectId);
  const folderById = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  const assetById = index.byId;
  const libraryName = (x: { projectId: string; spaceId: string }) =>
    x.projectId ? (projects.find((p) => p.id === x.projectId)?.name ?? 'Project files') : x.spaceId ? (sp.spaceById.get(x.spaceId)?.name ?? 'Space') : 'Shared library';
  const scopeName =
    scope === 'all' ? 'All files' : scope === 'library' ? 'Shared library' : scopeSpaceId ? (sp.spaceById.get(scopeSpaceId)?.name ?? 'Space') : (projects.find((p) => p.id === scopeProjectId)?.name ?? 'Project');

  /** Files, subfolders, sizes and the newest file of every folder, counting what is inside its subfolders. */
  const folderStats = useMemo(() => {
    const kids = new Map<string, string[]>();
    for (const f of folders) kids.set(f.parentId, [...(kids.get(f.parentId) ?? []), f.id]);
    const files = new Map<string, AssetRow[]>();
    for (const a of assets) if (a.folderId) files.set(a.folderId, [...(files.get(a.folderId) ?? []), a]);
    const memo = new Map<string, FolderStats>();
    const walk = (id: string, depth: number): FolderStats => {
      const done = memo.get(id);
      if (done) return done;
      const own = files.get(id) ?? [];
      const s: FolderStats = { files: own.length, folders: 0, size: 0, storage: 0, latest: undefined };
      for (const a of own) {
        s.size += a.sizeBytes;
        s.storage += a.totalBytes;
        if (!s.latest || a.createdAt.microsSinceUnixEpoch > s.latest.createdAt.microsSinceUnixEpoch) s.latest = a;
      }
      for (const k of depth < 64 ? (kids.get(id) ?? []) : []) {
        const c = walk(k, depth + 1);
        s.files += c.files;
        s.folders += 1 + c.folders;
        s.size += c.size;
        s.storage += c.storage;
        if (c.latest && (!s.latest || c.latest.createdAt.microsSinceUnixEpoch > s.latest.createdAt.microsSinceUnixEpoch)) s.latest = c.latest;
      }
      memo.set(id, s);
      return s;
    };
    return (id: string) => walk(id, 0);
  }, [folders, assets]);

  // ----- sorting (folders stay on top; columns they don't have sort them by name)
  const assetValue = (a: AssetRow, k: SortKey): SortValue =>
    k === 'name'
      ? a.name
      : k === 'type'
        ? kindLabel(a)
        : k === 'duration'
          ? a.durationUs || undefined
          : k === 'resolution'
            ? a.width * a.height || undefined
            : k === 'size'
              ? a.sizeBytes
              : k === 'createdBy'
                ? a.createdByName
                : a.createdAt.microsSinceUnixEpoch;
  const folderValue = (f: FolderRow, k: SortKey): SortValue =>
    k === 'size'
      ? folderStats(f.id).size
      : k === 'createdBy'
        ? f.createdByName
        : k === 'added'
          ? (folderStats(f.id).latest?.createdAt ?? f.createdAt).microsSinceUnixEpoch
          : f.name;
  const folderSorts = sort.key === 'name' || sort.key === 'size' || sort.key === 'createdBy' || sort.key === 'added';

  // Folders shared with someone who can't see their parent show at the top.
  const visibleFolders = query
    ? []
    : sortRows(
        folders.filter((f) => (f.parentId === folderId || (!folderId && f.parentId && !folderById.has(f.parentId))) && inScope(f)),
        (f) => (folderSorts ? folderValue(f, sort.key) : f.name),
        folderSorts ? sort.dir : 'asc',
      );
  const current = folderById.get(folderId);
  const integrations = useIntegrations(orgId);
  const hereScope = current
    ? current.scopeId
    : scopeProjectId
      ? (projects.find((p) => p.id === scopeProjectId)?.scopeId ?? '')
      : scopeSpaceId
        ? (sp.spaceById.get(scopeSpaceId)?.scopeId ?? '')
        : '';
  const canEdit = sp.canIn(hereScope, 'editor');
  const [access, setAccess] = useState<{ id: string; name: string } | null>(null);
  const visible = useMemo(
    () =>
      sortRows(
        [...assets]
          .filter(inScope)
          // Files in a folder the viewer can't see (shared through a project) show at the top.
          .filter((a) => (query ? true : a.folderId === folderId || (!folderId && a.folderId && !folderById.has(a.folderId))))
          .filter((a) => kind === 'all' || a.kind === kind)
          .filter((a) => a.name.toLowerCase().includes(query.toLowerCase()))
          .sort((a, b) => Number(b.createdAt.microsSinceUnixEpoch - a.createdAt.microsSinceUnixEpoch)),
        (a) => assetValue(a, sort.key),
        sort.dir,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assets, scope, folderId, query, kind, folderById, sort.key, sort.dir],
  );
  const breadcrumbs = useMemo(() => {
    const out: FolderRow[] = [];
    for (let cur = folderById.get(folderId), i = 0; cur && i < 64; cur = folderById.get(cur.parentId), i++) out.unshift(cur);
    return out;
  }, [folderById, folderId]);

  // ----- selection
  const order: Key[] = [...visibleFolders.map((f) => `f:${f.id}`), ...visible.map((a) => `a:${a.id}`)];
  const itemScope = (k: Key) => (k.startsWith('f:') ? folderById.get(k.slice(2))?.scopeId : assetById.get(k.slice(2))?.scopeId) ?? '';
  const canEditItem = (k: Key) => sp.canIn(itemScope(k), 'editor');
  const renameKey = (k: Key) => {
    if (!canEditItem(k)) return;
    const f = k.startsWith('f:') ? folderById.get(k.slice(2)) : undefined;
    const a = k.startsWith('a:') ? assetById.get(k.slice(2)) : undefined;
    if (f) setRenaming({ id: f.id, name: f.name, type: 'folder' });
    else if (a) setRenaming({ id: a.id, name: a.name, type: 'asset' });
  };
  const { sel, setSel, selected, targetsOf, toggle, setAll, onItemClick, onItemContextMenu, boxHandlers, boxElement, boxing, gridRef } = useSelection(order, {
    resetOn: [folderId, scope, query, kind],
    onDelete: (keys) => keys.every(canEditItem) && setDeleting(keys),
    onRename: renameKey,
  });

  // ----- downloading originals as one zip
  /** Files in the keys (folders expand to everything inside, kept in folders) that have an original to download. */
  const zipItemsOf = (keys: Key[]) => {
    const out: { id: string; path: string }[] = [];
    const walk = (parent: string, path: string, depth: number) => {
      if (depth > 64) return;
      for (const a of assets) if (a.folderId === parent) out.push({ id: a.id, path });
      for (const f of folders) if (f.parentId === parent) walk(f.id, path ? `${path}/${f.name}` : f.name, depth + 1);
    };
    for (const k of keys) {
      if (k.startsWith('f:')) {
        const f = folderById.get(k.slice(2));
        if (f) walk(f.id, f.name, 0);
      } else if (assetById.has(k.slice(2))) out.push({ id: k.slice(2), path: '' });
    }
    return out.filter((i) => {
      const a = assetById.get(i.id);
      return a && a.status !== 'uploading' && !isFolderLinkedKey(a.storageKey);
    });
  };
  const downloadSelected = (keys: Key[]) => {
    const items = zipItemsOf(keys);
    if (!items.length) return toast.info('Nothing to download: originals on a NAS and unfinished uploads are skipped');
    downloadZip(items, keys.length === 1 && keys[0].startsWith('f:') ? (folderById.get(keys[0].slice(2))?.name ?? 'media') : 'media');
    toast.success(`Zipping ${plural(items.length, 'original')}…`);
  };

  // ----- moving
  const moveProblem = (keys: Key[], dest: Dest): string => mediaMoveProblem(keys, dest, folders);
  const moveTo = async (keys: Key[], dest: Dest) => {
    if (await moveMediaItems(keys, dest, folders, assetById)) setSel(new Set());
  };
  /** The top of the library being browsed (undefined parts keep each item's library). */
  const rootDest = (): Dest =>
    scope === 'library' ? { folderId: '', projectId: '', spaceId: '' } : scopeSpaceId ? { folderId: '', projectId: '', spaceId: scopeSpaceId } : scopeProjectId ? { folderId: '', projectId: scopeProjectId } : { folderId: '' };
  const folderDest = (f: FolderRow): Dest => ({ folderId: f.id, projectId: f.projectId, spaceId: f.spaceId });

  const { dropOn, dragStart, dragEnd, dropTarget } = useDragMove<Dest>(MEDIA_DRAG, { problem: moveProblem, onDrop: (keys, dest) => void moveTo(keys, dest) });
  const onItemDragStart = (e: React.DragEvent, k: Key) => {
    if (!sel.has(k)) setSel(new Set([k]));
    dragStart(e, sel.has(k) ? selected : [k]);
  };

  const pickReplacement = (a: AssetRow) => {
    setReplacing(a);
    if (replaceInput.current) {
      replaceInput.current.accept = a.kind === 'font' ? '.ttf,.otf,.woff,.woff2' : a.kind === 'lut' ? '.cube' : a.kind === 'audio' ? 'audio/*,video/*' : `${a.kind}/*`;
      replaceInput.current.click();
    }
  };
  const onReplacementPicked = async (file: File | undefined) => {
    const a = replacing;
    setReplacing(null);
    if (!a || !file) return;
    const projects = index.usedBy.get(a.id)?.length ?? 0;
    if (await replaceFile(a.id, file)) {
      toast.success(
        projects
          ? `Replacing "${a.name}": ${plural(projects, 'project')} using it switch to the new file once it is processed`
          : `Replacing "${a.name}": it switches to the new file once it is processed`,
      );
    }
  };
  const stopReplacement = (a: AssetRow) => {
    cancelUpload(a.id);
    void attempt(() => reducers().cancelReplaceAsset({ id: a.id }));
  };

  const onFiles = (files: FileList | UploadInput[]) => {
    if (!org) return;
    void uploadFiles([...files], { orgId: org.id, projectId: scopeProjectId, folderId, spaceId: current?.spaceId ?? scopeSpaceId });
  };

  if (!ready) {
    return (
      <div className="flex justify-center py-24 text-muted">
        <Spinner />
      </div>
    );
  }

  const deleteLabel = (keys: Key[]) =>
    allLinked(keys, assetById)
      ? keys.length === 1
        ? 'Remove link'
        : `Remove ${plural(keys.length, 'link')}`
      : keys.length === 1
        ? keys[0].startsWith('f:')
          ? 'Delete folder'
          : 'Delete'
        : `Delete ${plural(keys.length, 'item')}`;
  const moveLabel = (keys: Key[]) => (keys.length === 1 ? 'Move…' : `Move ${plural(keys.length, 'item')}…`);

  // ----- actions. The row/card "…" menu and the right-click menu are built
  // here, once per kind of item: add new per-file actions to fileMenu.
  const folderMenu = (f: FolderRow): (MenuItem | 'sep')[] => {
    const targets = targetsOf(`f:${f.id}`);
    return sp.canIn(f.scopeId, 'editor')
      ? [
          { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenaming({ id: f.id, name: f.name, type: 'folder' }) },
          { label: moveLabel(targets), icon: <MoveRight size={14} />, onClick: () => setMoving(targets) },
          ...(sp.role !== 'guest' ? [{ label: 'Access…', icon: <Lock size={14} />, onClick: () => setAccess({ id: f.id, name: f.name }) }] : []),
          'sep',
          { label: deleteLabel(targets), icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting(targets) },
        ]
      : [];
  };
  const folderContext = (f: FolderRow): ContextEntry[] => {
    const items = folderMenu(f);
    return [{ label: 'Open', icon: <FolderOpen size={14} />, onClick: () => setFolderId(f.id) }, ...(items.length ? (['sep'] as const) : []), ...items];
  };
  /** Files among the keys (not folders), sent to an integration with a subfolder named after the open folder. */
  const sendTo = (keys: Key[]) => {
    const assetIds = keys.filter((k) => k.startsWith('a:')).map((k) => k.slice(2));
    if (!assetIds.length || !orgId) return;
    const first = assetById.get(assetIds[0]);
    openSendTo({ orgId, assetIds, folder: current?.name ?? '', label: assetIds.length === 1 ? `“${first?.name ?? 'file'}”` : `${assetIds.length} files` });
  };
  const fileMenu = (a: AssetRow): (MenuItem | 'sep')[] => {
    const targets = targetsOf(`a:${a.id}`);
    const editable = sp.canIn(a.scopeId, 'editor');
    return [
      {
        label: isFolderLinkedKey(a.storageKey) ? 'Download original (stays on the NAS)' : 'Download original',
        icon: <Download size={14} />,
        disabled: a.status === 'uploading' || isFolderLinkedKey(a.storageKey),
        onClick: async () => {
          const res = await api<{ urls: Record<string, { original?: string }> }>('/api/media/urls', {
            method: 'POST',
            json: { assetIds: [a.id], variants: ['original'] },
          });
          const url = res.urls[a.id]?.original;
          if (url) window.open(url, '_blank');
        },
      },
      ...(targets.length > 1
        ? [{ label: `Download ${targets.length} originals (zip)`, icon: <Download size={14} />, onClick: () => downloadSelected(targets) }]
        : []),
      ...(integrations.length
        ? [
            {
              label: targets.length > 1 ? `Send ${targets.length} to…` : 'Send to…',
              icon: <Send size={14} />,
              disabled: a.status !== 'ready' || isFolderLinkedKey(a.storageKey),
              onClick: () => sendTo(targets),
            },
          ]
        : []),
      ...(a.status === 'ready' && ['video', 'audio', 'image'].includes(a.kind)
        ? [{ label: 'Review overview', icon: <MessagesSquare size={14} />, onClick: () => navigate(`/orgs/${a.orgId}/assets/${a.id}/review`) }]
        : []),
      ...(editable
        ? [
            { label: 'Rename', icon: <Pencil size={14} />, onClick: () => setRenaming({ id: a.id, name: a.name, type: 'asset' as const }) },
            // Linked files are replaced in their storage connection (then reprocessed).
            ...(!a.sourceId ? [{ label: 'Replace file…', icon: <Replace size={14} />, disabled: a.status === 'uploading', onClick: () => pickReplacement(a) }] : []),
            ...(a.replacement
              ? [
                  {
                    label: parseReplacement(a.replacement)?.status === 'failed' ? 'Dismiss failed replacement' : 'Cancel replacement',
                    icon: <X size={14} />,
                    onClick: () => stopReplacement(a),
                  },
                ]
              : []),
            { label: 'Share for review…', icon: <Share2 size={14} />, disabled: a.status !== 'ready' || !['video', 'audio', 'image'].includes(a.kind), onClick: () => (setShareTab('links'), setSharing(a)) },
            { label: moveLabel(targets), icon: <MoveRight size={14} />, onClick: () => setMoving(targets) },
            ...(a.kind !== 'font' && (a.status === 'error' || a.status === 'ready')
              ? [{ label: 'Reprocess', icon: <RefreshCcw size={14} />, onClick: () => void attempt(() => reducers().reprocessAsset({ id: a.id }), 'Reprocessing started') }]
              : []),
            ...((a.kind === 'video' || a.kind === 'audio') && a.status === 'ready'
              ? [
                  {
                    label: 'Convert…',
                    icon: <ArrowRightLeft size={14} />,
                    onClick: () =>
                      setConverting(
                        targets
                          .map((t) => (t.startsWith('a:') ? assetById.get(t.slice(2)) : undefined))
                          .filter((x): x is AssetRow => !!x && (x.kind === 'video' || x.kind === 'audio') && x.status === 'ready' && sp.canIn(x.scopeId, 'editor')),
                      ),
                  },
                ]
              : []),
            ...(a.kind === 'video' && a.hasVideo && a.status === 'ready' && targets.length === 1
              ? [{ label: 'Make GIF…', icon: <ImagePlay size={14} />, onClick: () => setGifFrom(a) }]
              : []),
            'sep' as const,
            { label: deleteLabel(targets), icon: <Trash2 size={14} />, danger: true, onClick: () => setDeleting(targets) },
          ]
        : []),
    ];
  };
  const fileContext = (a: AssetRow): ContextEntry[] => [
    { label: 'Preview', icon: <Eye size={14} />, disabled: a.status !== 'ready', onClick: () => setPreview(a) },
    { label: 'Copy name', icon: <Copy size={14} />, onClick: () => void navigator.clipboard.writeText(a.name) },
    'sep',
    ...fileMenu(a),
  ];
  const openFile = (a: AssetRow) => a.status === 'ready' && setPreview(a);

  const anySel = sel.size > 0;
  const check = (k: Key, label: string, className?: string) => <SelectCheck checked={sel.has(k)} show={anySel} label={label} onChange={() => toggle(k)} className={className} />;

  /** Props every folder card or row shares: selection, dragging, dropping, menus. */
  const folderProps = (f: FolderRow) => {
    const k = `f:${f.id}`;
    return {
      'data-sel-key': k,
      'data-folder-card': f.id,
      'data-selected': sel.has(k) || undefined,
      draggable: sp.canIn(f.scopeId, 'editor'),
      onDragStart: (e: React.DragEvent) => onItemDragStart(e, k),
      onDragEnd: dragEnd,
      ...dropTarget(f.id, () => folderDest(f)),
      onClick: (e: React.MouseEvent) => onItemClick(e, k, () => setFolderId(f.id)),
      onDoubleClick: () => setFolderId(f.id),
      onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && e.target === e.currentTarget && setFolderId(f.id),
      onContextMenu: (e: React.MouseEvent) => {
        e.stopPropagation();
        onItemContextMenu(k);
        openContextMenu(e, folderContext(f));
      },
    };
  };
  const fileProps = (a: AssetRow) => {
    const k = `a:${a.id}`;
    return {
      'data-sel-key': k,
      'data-asset-card': a.id,
      'data-selected': sel.has(k) || undefined,
      draggable: sp.canIn(a.scopeId, 'editor'),
      onDragStart: (e: React.DragEvent) => onItemDragStart(e, k),
      onDragEnd: dragEnd,
      onClick: (e: React.MouseEvent) => onItemClick(e, k, () => openFile(a)),
      onDoubleClick: () => openFile(a),
      onKeyDown: (e: React.KeyboardEvent) => e.key === 'Enter' && e.target === e.currentTarget && openFile(a),
      onContextMenu: (e: React.MouseEvent) => {
        e.stopPropagation();
        onItemContextMenu(k);
        openContextMenu(e, fileContext(a));
      },
    };
  };
  const actionsMenu = (items: (MenuItem | 'sep')[], label: string) =>
    items.length ? (
      <div onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <Menu
          trigger={
            <Button variant="ghost" size="icon-sm" aria-label={label}>
              <MoreHorizontal size={14} />
            </Button>
          }
          items={items}
        />
      </div>
    ) : null;
  /** Second line under a file's name: its state while it (or a replacement) uploads or processes, else its type. */
  const fileState = (a: AssetRow) => {
    const rep = parseReplacement(a.replacement);
    const pct = progressOf(a) !== undefined ? ` ${Math.round((progressOf(a) ?? 0) * 100)}%` : '';
    return a.status === 'error' ? (
      <span className="text-danger" title={a.error}>
        Processing failed
      </span>
    ) : rep ? (
      rep.status === 'failed' ? (
        <span className="text-danger" title={rep.error} data-replace-failed>
          Replacing failed
        </span>
      ) : (
        <span title={`Replacing with ${rep.name}`} data-replacing>
          {rep.status === 'uploading' ? 'Uploading new version' : 'Processing new version'}
          {pct}
        </span>
      )
    ) : a.status === 'uploading' || a.status === 'processing' ? (
      <span>
        {a.status === 'uploading' ? 'Uploading' : 'Processing'}
        {pct}
      </span>
    ) : a.status === 'ready' ? reprocessState(a) : null;
  };
  const reprocessState = (a: AssetRow) => {
    const r = reprocessing.get(a.id);
    if (!r) return null;
    return r.status === 'failed' ? (
      <span className="text-danger" title={r.error} data-reprocess-failed>
        Reprocessing failed
      </span>
    ) : (
      <span data-reprocessing>{r.status === 'queued' ? 'Reprocessing: waiting for a worker' : `Reprocessing ${Math.round(r.progress * 100)}%`}</span>
    );
  };

  const empty = visibleFolders.length === 0 && visible.length === 0;

  return (
    <div
      onDragOver={(e) => {
        if (!canEdit || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(false);
        if (canEdit && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
      {...boxHandlers}
      onPointerCancel={boxHandlers.onPointerUp}
      onContextMenu={(e) =>
        canEdit &&
        !(e.target as HTMLElement).closest('[data-inspector]') &&
        openContextMenu(e, [
          { label: 'Upload files…', icon: <Upload size={14} />, onClick: () => fileInput.current?.click() },
          { label: 'Upload from a folder or drive…', icon: <HardDrive size={14} />, onClick: () => setDrives(true) },
          { label: 'New folder', icon: <FolderPlus size={14} />, onClick: () => setRenaming({ id: '', name: 'New folder', type: 'folder' }) },
          'sep',
          {
            label: 'Regenerate video proxies',
            icon: <RefreshCcw size={14} />,
            onClick: () => {
              const videos = assets.filter((a) => a.status === 'ready' && a.hasVideo && a.kind !== 'image');
              if (!videos.length) return toast.info('No processed videos to regenerate');
              void Promise.all(videos.map((a) => reducers().reprocessAsset({ id: a.id }))).then(
                () => toast.success(`Regenerating proxies for ${videos.length} video${videos.length === 1 ? '' : 's'}`),
                (e) => toast.error(errorMessage(e)),
              );
            },
          },
        ])
      }
      className={cx('relative flex min-h-[60vh] items-start gap-6 pb-20', boxing && 'select-none')}
    >
      <div className="min-w-0 flex-1">
        <PageHeader
          title="Asset library"
          subtitle={org ? `${assets.length} files · ${formatBytes(assets.reduce((s, a) => s + a.totalBytes, 0))}` : undefined}
          actions={
            canEdit ? (
              <>
                <Button onClick={() => setRenaming({ id: '', name: 'New folder', type: 'folder' })}>
                  <FolderPlus size={16} /> New folder
                </Button>
                <Menu
                  trigger={
                    <Button variant="primary" data-upload-menu>
                      <Upload size={16} /> Upload <ChevronDown size={14} />
                    </Button>
                  }
                  items={[
                    { label: 'Files…', icon: <Upload size={14} />, onClick: () => fileInput.current?.click() },
                    { label: 'From a folder, camera card or drive…', icon: <HardDrive size={14} />, onClick: () => setDrives(true) },
                  ]}
                />
                {drives ? <DrivesDialog onClose={() => setDrives(false)} onFiles={onFiles} /> : null}
                <Button onClick={() => navigate(`/orgs/${orgId}/sources`)} title="Company storage (buckets, NAS): link files in place or import them" data-open-sources>
                  <Server size={16} /> Sources
                </Button>
                <input
                  ref={replaceInput}
                  type="file"
                  hidden
                  data-replace-input
                  onChange={(e) => {
                    void onReplacementPicked(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={UPLOAD_ACCEPT}
                  hidden
                  onChange={(e) => {
                    if (e.target.files) onFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </>
            ) : (
              <Button onClick={() => navigate(`/orgs/${orgId}/sources`)} data-open-sources>
                <Server size={16} /> Sources
              </Button>
            )
          }
        />
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
            <Input className="pl-9" placeholder="Search files" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <Select value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Library" data-library>
            <option value="all">All files</option>
            {sp.role !== 'guest' ? <option value="library">Shared library</option> : null}
            {sp.spaces.map((s) => (
              <option key={s.id} value={`space:${s.id}`}>
                Space: {s.name}
                {s.restricted ? ' 🔒' : ''}
              </option>
            ))}
            {projects.map((p) => (
              <option key={p.id} value={`project:${p.id}`}>
                Project: {p.name}
              </option>
            ))}
          </Select>
          <Select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type">
            <option value="all">All types</option>
            <option value="video">Video</option>
            <option value="audio">Audio</option>
            <option value="image">Images</option>
            <option value="font">Fonts</option>
          </Select>
          <div className="ml-auto flex items-center gap-2">
            <ViewToggle value={view} onChange={setView} />
            <InspectorToggle open={inspector.open} onClick={inspector.toggle} />
          </div>
        </div>
        {!query ? (
          <div className="mb-4 flex flex-wrap items-center gap-1 text-sm" data-breadcrumb>
            <button
              data-crumb=""
              className={cx('rounded px-1.5 py-0.5 hover:bg-panel-3 data-[drop-target]:bg-accent/20 data-[drop-target]:ring-1 data-[drop-target]:ring-accent', !folderId ? 'text-fg' : 'text-muted')}
              onClick={() => setFolderId('')}
              {...dropTarget('crumb:', rootDest)}
            >
              {scopeName}
            </button>
            {breadcrumbs.map((b) => (
              <span key={b.id} className="flex items-center gap-1">
                <ChevronRight size={14} className="text-faint" />
                <button
                  data-crumb={b.id}
                  className={cx('rounded px-1.5 py-0.5 hover:bg-panel-3 hover:text-fg data-[drop-target]:bg-accent/20 data-[drop-target]:ring-1 data-[drop-target]:ring-accent', b.id === folderId ? 'text-fg' : 'text-muted')}
                  onClick={() => setFolderId(b.id)}
                  {...dropTarget(`crumb:${b.id}`, () => folderDest(b))}
                >
                  {b.name}
                </button>
              </span>
            ))}
          </div>
        ) : null}

        {empty ? (
          <EmptyState
            icon={<FolderOpen size={32} />}
            title="No files here"
            action={
              canEdit ? (
                <Button variant="primary" onClick={() => fileInput.current?.click()}>
                  <Upload size={16} /> Upload files
                </Button>
              ) : null
            }
          >
            Drag and drop video, audio and image files anywhere on this page. Large files upload in parallel chunks.
          </EmptyState>
        ) : view === 'table' ? (
          <div ref={gridRef} className={tableCls.wrap}>
            <table className={tableCls.table} data-asset-table>
              <thead>
                <tr className={tableCls.head}>
                  <th className="w-9 py-2 pl-3 pr-0 align-middle">
                    <SelectCheck checked={order.length > 0 && sel.size === order.length} mixed={sel.size > 0 && sel.size < order.length} show label="Select all" onChange={setAll} />
                  </th>
                  <SortTh k="name" sort={sort} onSort={onSort} first="asc" className="w-full">
                    Name
                  </SortTh>
                  <SortTh k="type" sort={sort} onSort={onSort} first="asc" className="hidden sm:table-cell">
                    Type
                  </SortTh>
                  <SortTh k="duration" sort={sort} onSort={onSort} align="right" className="hidden md:table-cell">
                    Duration
                  </SortTh>
                  <SortTh k="resolution" sort={sort} onSort={onSort} className="hidden lg:table-cell">
                    Resolution
                  </SortTh>
                  <SortTh k="size" sort={sort} onSort={onSort} align="right">
                    Size
                  </SortTh>
                  <SortTh k="createdBy" sort={sort} onSort={onSort} first="asc" className="hidden xl:table-cell">
                    Added by
                  </SortTh>
                  <SortTh k="added" sort={sort} onSort={onSort} className="hidden md:table-cell">
                    Added
                  </SortTh>
                  <th className="w-10 px-1" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visibleFolders.map((f) => {
                  const s = folderStats(f.id);
                  return (
                    <tr key={f.id} tabIndex={0} {...folderProps(f)} className={cx(tableCls.row, rowTone(sel.has(`f:${f.id}`), dropOn === f.id))}>
                      <td className="w-9 py-1.5 pl-3 pr-0">{check(`f:${f.id}`, `Select ${f.name}`)}</td>
                      <td className="max-w-0 px-3 py-1.5">
                        <div className="flex items-center gap-3" data-asset-folder={f.id}>
                          <div className="flex aspect-video w-16 shrink-0 items-center justify-center rounded-md bg-panel-2 text-accent-2">
                            <Folder size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate font-medium">{f.name}</span>
                              {f.restricted ? <Lock size={12} className="shrink-0 text-faint" /> : null}
                            </div>
                            <div className="truncate text-xs text-faint">
                              {plural(s.files, 'file')}
                              {s.folders ? ` · ${plural(s.folders, 'folder')}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={cx(tableCls.cell, 'hidden sm:table-cell')}>Folder</td>
                      <td className={cx(tableCls.cell, 'hidden text-right md:table-cell')} />
                      <td className={cx(tableCls.cell, 'hidden lg:table-cell')} />
                      <td className={cx(tableCls.cell, 'text-right')}>{formatBytes(s.size)}</td>
                      <td className={cx(tableCls.cell, 'hidden xl:table-cell')}>{f.createdByName || '—'}</td>
                      <td className={cx(tableCls.cell, 'hidden md:table-cell')}>{timeAgo(s.latest?.createdAt ?? f.createdAt)}</td>
                      <td className="px-1 py-1.5">{actionsMenu(folderMenu(f), 'Folder actions')}</td>
                    </tr>
                  );
                })}
                {visible.map((a) => (
                  <tr key={a.id} tabIndex={0} {...fileProps(a)} className={cx(tableCls.row, rowTone(sel.has(`a:${a.id}`), false))}>
                    <td className="w-9 py-1.5 pl-3 pr-0">{check(`a:${a.id}`, `Select ${a.name}`)}</td>
                    <td className="max-w-0 px-3 py-1.5">
                      <div className="flex items-center gap-3">
                        <AssetThumb asset={a} progress={progressOf(a)} compact className="w-16 shrink-0 rounded-md" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium" title={a.name}>
                              {a.name}
                            </span>
                            <AssetReviewBadge review={reviews.get(a.id)} />
                            <LinkedBadge asset={a} />
                          </div>
                          <div className="truncate text-xs text-faint">{fileState(a) ?? <span className="sm:hidden">{kindLabel(a)}</span>}</div>
                        </div>
                      </div>
                    </td>
                    <td className={cx(tableCls.cell, 'hidden sm:table-cell')}>{kindLabel(a)}</td>
                    <td className={cx(tableCls.cell, 'hidden text-right font-mono text-xs md:table-cell')}>{a.durationUs ? formatDuration(a.durationUs) : ''}</td>
                    <td className={cx(tableCls.cell, 'hidden lg:table-cell')}>
                      {a.width ? `${a.width}×${a.height}` : ''}
                      {a.fps && a.kind === 'video' ? <span className="text-faint"> · {Math.round(a.fps * 100) / 100} fps</span> : null}
                    </td>
                    <td className={cx(tableCls.cell, 'text-right')}>{formatBytes(a.sizeBytes)}</td>
                    <td className={cx(tableCls.cell, 'hidden xl:table-cell')}>{a.createdByName || '—'}</td>
                    <td className={cx(tableCls.cell, 'hidden md:table-cell')}>{timeAgo(a.createdAt)}</td>
                    <td className="px-1 py-1.5">{actionsMenu(fileMenu(a), 'File actions')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div ref={gridRef} className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(190px,1fr))] sm:gap-4">
            {visibleFolders.map((f) => (
              <div
                key={f.id}
                {...folderProps(f)}
                className={cx(
                  'group relative flex cursor-pointer select-none flex-col rounded-xl border bg-panel',
                  sel.has(`f:${f.id}`) ? 'border-accent ring-2 ring-accent/60' : 'border-line hover:border-line-2',
                  dropOn === f.id && 'border-accent bg-accent/10 ring-2 ring-accent',
                )}
              >
                {check(`f:${f.id}`, `Select ${f.name}`, 'absolute left-2 top-2 z-10 shadow')}
                <div className="flex aspect-video items-center justify-center rounded-t-xl bg-panel-2 text-accent-2">
                  <Folder size={36} />
                </div>
                <div className="flex items-center gap-1 p-2.5" data-asset-folder={f.id}>
                  <div className="flex flex-1 items-center gap-1.5 truncate text-sm font-medium">
                    {f.name} {f.restricted ? <Lock size={12} className="shrink-0 text-faint" /> : null}
                  </div>
                  {actionsMenu(folderMenu(f), 'Folder actions')}
                </div>
              </div>
            ))}
            {visible.map((a) => (
              <div
                key={a.id}
                {...fileProps(a)}
                className={cx('group relative cursor-pointer select-none overflow-hidden rounded-xl border bg-panel', sel.has(`a:${a.id}`) ? 'border-accent ring-2 ring-accent/60' : 'border-line hover:border-line-2')}
              >
                {check(`a:${a.id}`, `Select ${a.name}`, 'absolute left-2 top-2 z-10 shadow')}
                <AssetThumb asset={a} progress={progressOf(a)} />
                <div className="flex items-start gap-1 p-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium" title={a.name}>
                        {a.name}
                      </span>
                      <AssetReviewBadge review={reviews.get(a.id)} />
                      <LinkedBadge asset={a} />
                    </div>
                    <div className="truncate text-xs text-faint">
                      {a.status === 'error' || a.replacement || reprocessing.has(a.id) ? (
                        fileState(a)
                      ) : (
                        <>
                          {a.width ? `${a.width}×${a.height} · ` : ''}
                          {formatBytes(a.sizeBytes)} · {timeAgo(a.createdAt)}
                        </>
                      )}
                    </div>
                  </div>
                  {actionsMenu(fileMenu(a), 'File actions')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InspectorPanel open={inspector.open} wide={inspector.wide} onClose={() => inspector.setOpen(false)}>
        <AssetsInspector
          selected={selected}
          index={index}
          folderById={folderById}
          folderStats={folderStats}
          libraryName={libraryName}
          scopeAssets={current ? [] : assets.filter(inScope)}
          scopeName={scopeName}
          folderId={folderId}
          sp={sp}
          progress={progressOf}
          reviews={reviews}
          onShare={(a, tab) => {
            setShareTab(tab ?? 'links');
            setSharing(a as AssetRow);
          }}
        />
      </InspectorPanel>

      {boxElement}

      {selected.length ? (
        <div
          data-selection-bar
          className="fixed bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded-xl border border-line-2 bg-panel-2 px-2 py-1.5 text-sm shadow-xl"
        >
          <span className="px-2 font-medium">{plural(selected.length, 'item')} selected</span>
          <Button size="sm" variant="ghost" onClick={() => downloadSelected(selected)} data-download-selected>
            <Download size={14} /> Download
          </Button>
          {integrations.length && selected.some((k) => k.startsWith('a:')) ? (
            <Button size="sm" variant="ghost" onClick={() => sendTo(selected)} data-send-selected>
              <Send size={14} /> Send to…
            </Button>
          ) : null}
          {selected.every(canEditItem) ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setMoving(selected)} data-move-selected>
                <MoveRight size={14} /> Move…
              </Button>
              <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDeleting(selected)}>
                <Trash2 size={14} /> Delete
              </Button>
            </>
          ) : null}
          <Button size="icon-sm" variant="ghost" aria-label="Clear selection" onClick={() => setSel(new Set())}>
            <X size={14} />
          </Button>
        </div>
      ) : null}

      {dragging ? (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-2xl border-2 border-dashed border-accent bg-accent/10 text-lg font-medium text-accent-2">
          Drop files to upload
        </div>
      ) : null}

      <PreviewModal asset={preview} onClose={() => setPreview(null)} />
      {sharing ? <ShareAssetDialog asset={sharing} open onClose={closeShare} commentId={sharing.id === shareParam ? commentParam : ''} tab={shareTab} /> : null}
      <ConvertDialog assets={converting} onClose={() => setConverting(null)} />
      <GifDialog asset={gifFrom} onClose={() => setGifFrom(null)} />
      <NameModal
        state={renaming}
        onClose={() => setRenaming(null)}
        onSave={async (name) => {
          if (!renaming || !org) return false;
          if (renaming.type === 'folder' && !renaming.id) {
            return attempt(() => reducers().createFolder({ id: uuidv7(), orgId: org.id, projectId: current?.projectId ?? scopeProjectId, parentId: folderId, name, spaceId: current?.spaceId ?? scopeSpaceId }));
          }
          if (renaming.type === 'folder') return attempt(() => reducers().renameFolder({ id: renaming.id, name }));
          return attempt(() => reducers().updateAsset({ id: renaming.id, name, folderId: undefined, projectId: undefined, spaceId: undefined }));
        }}
      />
      <MoveModal
        keys={moving}
        assets={assetById}
        folders={folders}
        projects={projects.map((p) => ({ id: p.id, name: p.name }))}
        spaces={sp.spaces.map((s) => ({ id: s.id, name: s.name }))}
        problem={moveProblem}
        onMove={(dest) => moveTo(moving ?? [], dest)}
        onClose={() => setMoving(null)}
      />
      {access && org ? (
        <AccessDialog
          orgId={org.id}
          kind="asset_folder"
          id={access.id}
          name={access.name}
          restricted={folderById.get(access.id)?.restricted ?? false}
          open
          onClose={() => setAccess(null)}
        />
      ) : null}
      <ConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={
          deleting && allLinked(deleting, assetById)
            ? deleting.length > 1
              ? `Remove ${plural(deleting.length, 'link')}`
              : 'Remove link'
            : deleting && deleting.length > 1
              ? `Delete ${plural(deleting.length, 'item')}`
              : deleting?.[0]?.startsWith('f:')
                ? 'Delete folder'
                : 'Delete file'
        }
        danger
        confirmLabel={deleting && allLinked(deleting, assetById) ? 'Remove' : 'Delete'}
        message={<DeleteMessage keys={deleting ?? []} assets={assetById} folders={folderById} />}
        onConfirm={async () => {
          const keys = deleting ?? [];
          const ok = await attempt(async () => {
            for (const k of keys) {
              if (k.startsWith('a:')) await reducers().deleteAsset({ id: k.slice(2), force: true });
              else await reducers().deleteFolder({ id: k.slice(2) });
            }
          }, allLinked(keys, assetById) ? (keys.length > 1 ? `Removed ${plural(keys.length, 'link')}` : 'Link removed') : keys.length > 1 ? `Deleted ${plural(keys.length, 'item')}` : 'Deleted');
          if (ok) setSel(new Set());
        }}
      />
    </div>
  );
}

/** Every item is a file linked from a storage connection (removing it never touches the original). */
function allLinked(keys: Key[], assets: Map<string, AssetRow>): boolean {
  return keys.length > 0 && keys.every((k) => k.startsWith('a:') && !!assets.get(k.slice(2))?.sourceId);
}

function DeleteMessage({ keys, assets, folders }: { keys: Key[]; assets: Map<string, AssetRow>; folders: Map<string, FolderRow> }) {
  const files = keys.filter((k) => k.startsWith('a:'));
  const linked = files.filter((k) => !!assets.get(k.slice(2))?.sourceId).length;
  const dirs = keys.filter((k) => k.startsWith('f:'));
  const name = keys.length === 1 ? (files.length ? assets.get(files[0].slice(2))?.name : folders.get(dirs[0].slice(2))?.name) : '';
  return (
    <div className="space-y-2">
      {files.length ? (
        <p>
          {name ? <b className="text-fg">{name}</b> : <b className="text-fg">{plural(files.length, 'file')}</b>} and {files.length === 1 ? 'its' : 'their'} proxies are deleted. Clips using{' '}
          {files.length === 1 ? 'it' : 'them'} are removed from timelines.
        </p>
      ) : null}
      {linked ? (
        <p>
          {linked === files.length ? (files.length === 1 ? 'It is linked' : 'They are linked') : `${plural(linked, 'file')} ${linked === 1 ? 'is' : 'are'} linked`} from a
          storage connection: only vidmakr’s link and previews are removed. The original stays on the NAS or in the bucket, untouched, and can be linked again later.
        </p>
      ) : null}
      {dirs.length ? (
        <p>
          {name && !files.length ? <b className="text-fg">{name}</b> : <b className="text-fg">{plural(dirs.length, 'folder')}</b>} {dirs.length === 1 ? 'is' : 'are'} removed; what’s
          inside moves up a level.
        </p>
      ) : null}
    </div>
  );
}

/** Picks where to move files and folders: any library, at its top or in any of its folders. */
function MoveModal({
  keys,
  assets,
  folders,
  projects,
  spaces,
  problem,
  onMove,
  onClose,
}: {
  keys: Key[] | null;
  assets: Map<string, AssetRow>;
  folders: readonly FolderRow[];
  projects: { id: string; name: string }[];
  spaces: { id: string; name: string }[];
  problem: (keys: Key[], dest: Dest) => string;
  onMove: (dest: Dest) => Promise<void>;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastKeys, setLastKeys] = useState<Key[] | null>(null);
  const byId = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  // Every library with its folders as indented paths.
  const groups = useMemo(() => {
    const libs = [
      { label: 'Shared library', projectId: '', spaceId: '' },
      ...spaces.map((s) => ({ label: `Space: ${s.name}`, projectId: '', spaceId: s.id })),
      ...projects.map((p) => ({ label: `Project: ${p.name}`, projectId: p.id, spaceId: '' })),
    ];
    return libs.map((lib) => {
      const inLib = folders.filter((f) => (lib.projectId ? f.projectId === lib.projectId : !f.projectId && f.spaceId === lib.spaceId));
      const ids = new Set(inLib.map((f) => f.id));
      const children = (parentId: string) => inLib.filter((f) => (parentId ? f.parentId === parentId : !ids.has(f.parentId))).sort((a, b) => a.name.localeCompare(b.name));
      const rows: { f: FolderRow; depth: number }[] = [];
      const walk = (parentId: string, depth: number) => {
        for (const f of children(parentId)) {
          rows.push({ f, depth });
          if (depth < 32) walk(f.id, depth + 1);
        }
      };
      walk('', 0);
      return { ...lib, rows };
    });
  }, [folders, projects, spaces]);
  const destOf = (v: string): Dest | null => {
    if (v.startsWith('folder:')) {
      const f = byId.get(v.slice(7));
      return f ? { folderId: f.id, projectId: f.projectId, spaceId: f.spaceId } : null;
    }
    if (v.startsWith('top:')) {
      const [projectId, spaceId] = v.slice(4).split('|');
      return { folderId: '', projectId, spaceId: projectId ? undefined : spaceId };
    }
    return null;
  };
  if (keys && keys !== lastKeys) {
    setLastKeys(keys);
    // Start where a single file is now.
    const one = keys.length === 1 && keys[0].startsWith('a:') ? assets.get(keys[0].slice(2)) : undefined;
    setValue(one ? (one.folderId ? `folder:${one.folderId}` : `top:${one.projectId}|${one.spaceId}`) : '');
  }
  const dest = destOf(value);
  const why = keys && dest ? problem(keys, dest) : '';
  const allowed = (d: Dest) => !keys || !problem(keys, d);
  return (
    <Modal
      open={!!keys}
      onClose={onClose}
      title={keys && keys.length > 1 ? `Move ${plural(keys.length, 'item')}` : 'Move'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!dest || !!why}
            data-move-confirm
            onClick={async () => {
              if (!dest) return;
              setBusy(true);
              await onMove(dest);
              setBusy(false);
              onClose();
            }}
          >
            Move
          </Button>
        </>
      }
    >
      <div className="mb-1.5 text-xs font-medium text-muted">Destination</div>
      <Select className="w-full" value={value} onChange={(e) => setValue(e.target.value)} data-move-dest>
        <option value="" disabled>
          Choose a library or folder…
        </option>
        {groups.map((g) => {
          const top: Dest = { folderId: '', projectId: g.projectId, spaceId: g.projectId ? undefined : g.spaceId };
          return (
            <optgroup key={`${g.projectId}|${g.spaceId}`} label={g.label}>
              <option value={`top:${g.projectId}|${g.spaceId}`} disabled={!allowed(top)}>
                {g.label} (top level)
              </option>
              {g.rows.map(({ f, depth }) => (
                <option key={f.id} value={`folder:${f.id}`} disabled={!allowed({ folderId: f.id, projectId: f.projectId, spaceId: f.spaceId })}>
                  {'   '.repeat(depth + 1)}
                  {f.name}
                </option>
              ))}
            </optgroup>
          );
        })}
      </Select>
      {why ? <p className="mt-2 text-xs text-danger">{why}</p> : null}
    </Modal>
  );
}

function NameModal({
  state,
  onClose,
  onSave,
}: {
  state: { id: string; name: string; type: 'asset' | 'folder' } | null;
  onClose: () => void;
  onSave: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const key = state ? `${state.type}:${state.id}` : '';
  const [lastKey, setLastKey] = useState('');
  if (key !== lastKey) {
    setLastKey(key);
    setName(state?.name ?? '');
  }
  const save = async () => {
    setBusy(true);
    const ok = await onSave(name);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal
      open={!!state}
      onClose={onClose}
      title={state?.type === 'folder' ? (state.id ? 'Rename folder' : 'New folder') : 'Rename file'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && save()} />
    </Modal>
  );
}

function PreviewModal({ asset, onClose }: { asset: AssetRow | null; onClose: () => void }) {
  const [url, setUrl] = useState<string>('');
  const [lastId, setLastId] = useState('');
  if (asset && asset.id !== lastId) {
    setLastId(asset.id);
    setUrl('');
    if (asset.kind === 'font') void ensureCustomFont(asset.id).then(() => setUrl('font'));
    else void mediaUrls(asset.id, asset.proxyKey).then((u) => setUrl(u.proxy ?? ''));
  }
  return (
    <Modal open={!!asset} onClose={onClose} title={asset?.name ?? ''} width={900}>
      {!url ? (
        <div className="flex justify-center py-16 text-muted">
          <Spinner />
        </div>
      ) : asset?.kind === 'font' ? (
        <div className="space-y-3 overflow-hidden break-words py-4 text-fg" style={{ fontFamily: `"${customFontFamily(asset.id)}"` }}>
          <div className="text-5xl">The quick brown fox jumps over the lazy dog</div>
          <div className="text-2xl">ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789 !?&amp;.,</div>
        </div>
      ) : asset?.kind === 'image' ? (
        <img src={url} alt="" className="mx-auto max-h-[70vh] rounded-lg" />
      ) : asset?.kind === 'audio' ? (
        <audio src={url} controls autoPlay className="w-full" />
      ) : (
        <video src={url} controls autoPlay className="max-h-[70vh] w-full rounded-lg bg-black" />
      )}
      {asset ? (
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted sm:grid-cols-4">
          {asset.width ? <div>Resolution: {asset.width}×{asset.height}</div> : null}
          {asset.fps ? <div>Frame rate: {asset.fps} fps</div> : null}
          {asset.videoCodec ? <div>Video: {asset.videoCodec}</div> : null}
          {asset.audioCodec ? <div>Audio: {asset.audioCodec}</div> : null}
          <div>Size: {formatBytes(asset.sizeBytes)}</div>
          <div>Uploaded by {asset.createdByName}</div>
        </div>
      ) : null}
    </Modal>
  );
}

/** Media linked in place from a storage connection: the original stays there (and may go missing). */
function LinkedBadge({ asset }: { asset: AssetRow }) {
  if (!asset.sourceId) return null;
  return asset.sourceMissing ? (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded bg-danger/15 px-1.5 py-0.5 text-[10px] font-medium text-danger"
      title="The original can no longer be read from its storage connection (removed, moved or renamed). Proxies still play; exports need the original."
      data-linked-missing
    >
      <Link2 size={10} /> Original missing
    </span>
  ) : (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent-2"
      title={
        isFolderLinkedKey(asset.storageKey)
          ? 'Kept on the NAS: only its preview is in vidmakr; exports using it run on the worker that shares the folder'
          : 'Linked in place: the original stays in its storage connection'
      }
      data-linked
    >
      <Link2 size={10} /> {isFolderLinkedKey(asset.storageKey) ? 'On NAS' : 'Linked'}
    </span>
  );
}
